// Editor store: the open Label, derived Field list, selection, view state, undo history.
// Every mutation goes through mutate()/mutateLive()+commit() so history and dirty tracking stay honest.
import { openLabel, saveLabel, readFields } from '../itf/label.js';

const HISTORY_MAX = 200;
const COALESCE_MS = 900;

export const S = {
  label: null,
  fields: [],
  byKey: new Map(),
  filename: 'untitled.itf',
  fileHandle: null, // FileSystemFileHandle when opened/saved via the File System Access API
  savedAt: null,
  selection: [], // field keys
  hovered: null,
  zoom: 1,
  showGrid: readPref('labels.grid', true),
  showRulers: readPref('labels.rulers', true),
  leftCollapsed: false,
  rightCollapsed: false,
  filter: '',
  dpiRatio: parseFloat(safeGet('labels.dpiRatio')) || null, // CSS px per file unit when calibrated
  lockedIds: new Set(), // editor-only locks, keyed by field ID (survives undo re-open)
  issues: [],
  // transient canvas overlays
  guides: null,
  marquee: null,
};

function safeGet(k) {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
export function safeSet(k, v) {
  try {
    if (v === null) localStorage.removeItem(k);
    else localStorage.setItem(k, String(v));
  } catch {
    /* private mode */
  }
}
function readPref(k, dflt) {
  const v = safeGet(k);
  return v === null ? dflt : v === 'true';
}

// ----- subscribe / emit (batched per microtask) ------------------------------
const listeners = new Set();
let pending = null;
/** fn(reasons: Set<'doc'|'selection'|'view'|'file'|'history'|'issues'|'hover'|'overlay'>) */
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function emit(...reasons) {
  if (!pending) {
    pending = new Set();
    queueMicrotask(() => {
      const r = pending;
      pending = null;
      for (const fn of listeners) {
        try {
          fn(r);
        } catch (err) {
          console.error(err);
        }
      }
    });
  }
  for (const r of reasons) pending.add(r);
}

// ----- fields ---------------------------------------------------------------
function refreshFields() {
  S.fields = S.label ? readFields(S.label) : [];
  S.byKey = new Map(S.fields.map((f) => [f.key, f]));
  S.selection = S.selection.filter((k) => S.byKey.has(k));
  if (S.hovered && !S.byKey.has(S.hovered)) S.hovered = null;
}
export const field = (key) => S.byKey.get(key) || null;
export const selectedFields = () => S.selection.map((k) => S.byKey.get(k)).filter(Boolean);
export const singleSelected = () => (S.selection.length === 1 ? field(S.selection[0]) : null);
export const isUnsupported = (f) => !!f && f.kind === 'unsupported';
/** Editor-lock identity: the file ID survives undo/redo re-opens (keys do not). */
const lockId = (f) => (f.id ?? `name:${f.name}`);
export const isLocked = (f) => !!f && (f.kind === 'unsupported' || f.locked === true || S.lockedIds.has(lockId(f)));
export const isEditorLocked = (f) => !!f && S.lockedIds.has(lockId(f));
export const editableSelection = () => selectedFields().filter((f) => !isLocked(f));

export function setLocked(f, on) {
  if (!f || f.kind === 'unsupported') return;
  if (on) S.lockedIds.add(lockId(f));
  else S.lockedIds.delete(lockId(f));
  emit('doc', 'selection');
}

// ----- history ----------------------------------------------------------------
let history = []; // { bytes, selIds }
let index = -1;
let savedIndex = -1;
let lastTag = null;
let lastTime = 0;

const selIds = () => selectedFields().map((f) => f.id);

function snapshot() {
  return { bytes: saveLabel(S.label), selIds: selIds() };
}

/** Record the current label state. Same `tag` within COALESCE_MS replaces the top entry (typing bursts). */
export function commit(tag = null) {
  if (!S.label) return;
  const now = Date.now();
  const snap = snapshot();
  const top = history[index];
  if (top && bytesEqual(top.bytes, snap.bytes)) {
    top.selIds = snap.selIds;
    emit('history');
    return;
  }
  if (tag && tag === lastTag && now - lastTime < COALESCE_MS && index === history.length - 1 && index > 0) {
    history[index] = snap;
    if (savedIndex === index) savedIndex = -1;
  } else {
    history.length = index + 1;
    history.push(snap);
    if (history.length > HISTORY_MAX) {
      history.shift();
      savedIndex--;
    }
    index = history.length - 1;
  }
  lastTag = tag;
  lastTime = now;
  emit('history', 'file');
}

function bytesEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

export const canUndo = () => index > 0;
export const canRedo = () => index >= 0 && index < history.length - 1;
export const isDirty = () => !!S.label && index !== savedIndex;

function restore(i) {
  const snap = history[i];
  if (!snap) return;
  index = i;
  lastTag = null;
  S.label = openLabel(snap.bytes);
  S.selection = [];
  refreshFields();
  const want = new Set(snap.selIds);
  S.selection = S.fields.filter((f) => want.has(f.id)).map((f) => f.key);
  emit('doc', 'selection', 'history', 'file');
}
export function undo() {
  if (canUndo()) restore(index - 1);
}
export function redo() {
  if (canRedo()) restore(index + 1);
}

// ----- lifecycle --------------------------------------------------------------
export function loadLabel(label, { filename = 'untitled.itf', handle = null, saved = true } = {}) {
  S.label = label;
  S.filename = filename;
  S.fileHandle = handle;
  S.selection = [];
  S.hovered = null;
  S.lockedIds = new Set();
  S.issues = [];
  refreshFields();
  history = [snapshot()];
  index = 0;
  savedIndex = saved ? 0 : -1;
  lastTag = null;
  S.savedAt = saved ? Date.now() : null;
  emit('doc', 'selection', 'history', 'file', 'view');
}

export function markSaved({ filename, handle } = {}) {
  if (filename) S.filename = filename;
  if (handle !== undefined) S.fileHandle = handle;
  savedIndex = index;
  S.savedAt = Date.now();
  emit('file');
}

/**
 * Apply a change to the label. fn(label) may return a key (or keys) to select.
 * opts.tag coalesces bursts (e.g. typing into one input); opts.live skips the history commit
 * (call commit() yourself when the gesture ends).
 */
export function mutate(fn, { tag = null, live = false } = {}) {
  if (!S.label) return undefined;
  let result;
  try {
    result = fn(S.label);
  } catch (err) {
    refreshFields();
    emit('doc');
    throw err;
  }
  refreshFields();
  if (result !== undefined && result !== null && result !== false) {
    const keys = (Array.isArray(result) ? result : [result]).filter((k) => typeof k === 'string' && S.byKey.has(k));
    if (keys.length) {
      S.selection = keys;
      emit('selection');
    }
  }
  if (!live) commit(tag);
  emit('doc', 'file');
  return result;
}

// ----- selection ----------------------------------------------------------------
export function select(keys, { additive = false, toggle = false } = {}) {
  const list = (Array.isArray(keys) ? keys : keys == null ? [] : [keys]).filter((k) => S.byKey.has(k));
  if (toggle) {
    const set = new Set(S.selection);
    for (const k of list) set.has(k) ? set.delete(k) : set.add(k);
    S.selection = [...set];
  } else if (additive) {
    S.selection = [...new Set([...S.selection, ...list])];
  } else {
    S.selection = list;
  }
  emit('selection');
}

export function setView(patch) {
  Object.assign(S, patch);
  if ('showGrid' in patch) safeSet('labels.grid', S.showGrid);
  if ('showRulers' in patch) safeSet('labels.rulers', S.showRulers);
  emit('view');
}
